import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Checkbox,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import PaymentDataIntegrity from './PaymentDataIntegrity';

interface Transaction {
  _id: string;
  client: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  amount: number;
  currency: string;
  status: string;
  type: string;
  createdAt: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  refundId?: string;
  refundReason?: string;
}

interface DashboardData {
  totalRevenue: number;
  totalTransactions: number;
  totalPaidSessions: number;
  totalUnpaidSessions: number;
  totalOutstanding: number;
  recentTransactions: Transaction[];
  outstandingPayments: OutstandingPayment[];
}

interface OutstandingPayment {
  _id: string;
  client: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    paymentDetails?: {
      type: string;
      employerReference?: string;
    };
  };
  amount: number;
  sessionDate: string;
  daysOverdue: number;
}

interface RefundDialogProps {
  open: boolean;
  transactionId: string;
  onClose: () => void;
  onRefund: (transactionId: string, reason: string) => Promise<void>;
}

const RefundDialog: React.FC<RefundDialogProps> = ({ open, transactionId, onClose, onRefund }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRefund = async () => {
    if (!reason.trim()) {
      setError('Please provide a reason for the refund');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await onRefund(transactionId, reason);
      onClose();
    } catch (err) {
      setError('Failed to process refund');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        }
      }}
    >
      <DialogTitle sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Refund Payment</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          autoFocus
          margin="dense"
          label="Refund Reason"
          fullWidth
          multiline
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{
            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            '& .MuiOutlinedInput-root': {
              color: 'rgba(255, 255, 255, 0.9)',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(100, 181, 246, 0.5)' }
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleRefund} 
          disabled={loading}
          sx={{
            backgroundColor: 'rgba(244, 67, 54, 0.2)',
            color: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.3)',
              borderColor: 'rgba(244, 67, 54, 0.5)'
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)'
            }
          }}
        >
          {loading ? 'Processing...' : 'Refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PaymentDashboard: React.FC = () => {
  const api = useApi();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string>('');
  const [selectedClients, setSelectedClients] = useState<Set<string>>(new Set());
  const [sendingBulkRequest, setSendingBulkRequest] = useState(false);
  const [bulkRequestSuccess, setBulkRequestSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await api.get('/api/payments/payment-dashboard/dashboard-data');
      setDashboardData((response.data as any).data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    }
  }, [api]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  const handleRefund = async (transactionId: string, reason: string) => {
    try {
      const response = await api.post(`/api/payments/transactions/${transactionId}/refund`, { reason });
      if (response.error) {
        throw new Error(response.error);
      }
      setError('');
      fetchDashboardData(); // Refresh all data after refund
    } catch (err) {
      console.error('Error processing refund:', err);
      setError('Failed to process refund');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount); // Amount is already in pounds
  };

  const handleSelectClient = (clientId: string) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const handleSelectAll = () => {
    if (!dashboardData?.outstandingPayments) return;
    
    if (selectedClients.size === dashboardData.outstandingPayments.length) {
      setSelectedClients(new Set());
    } else {
      const allClientIds = new Set(dashboardData.outstandingPayments.map(p => p.client._id));
      setSelectedClients(allClientIds);
    }
  };

  const handleSendBulkPaymentRequests = async () => {
    if (selectedClients.size === 0) return;

    try {
      setSendingBulkRequest(true);
      setError(null);
      setBulkRequestSuccess(null);

      const response = await api.post('/api/payments/send-payment-requests-bulk', {
        clientIds: Array.from(selectedClients)
      });

      const data = response.data as { message?: string };
      setBulkRequestSuccess(data.message || 'Payment requests sent successfully');
      setSelectedClients(new Set()); // Clear selection

      // Clear success message after 5 seconds
      setTimeout(() => {
        setBulkRequestSuccess(null);
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send bulk payment requests');
    } finally {
      setSendingBulkRequest(false);
    }
  };

  const runQuickFix = async () => {
    try {
      setError(null);
      const response = await api.get('/api/payment-validation/validate-all');
      const data = response.data as { summary: { clientsWithIssues: number } };
      
      if (data.summary.clientsWithIssues > 0) {
        // Switch to Data Integrity tab to show details
        setActiveTab(1);
      } else {
        setError('All payment data is valid - no fixes needed!');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check payment data');
    }
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.2)', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 600,
              '&.Mui-selected': {
                color: 'rgba(255, 255, 255, 0.95)'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'rgba(79, 70, 229, 0.9)'
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Data Integrity" />
        </Tabs>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {activeTab === 0 && dashboardData && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <Card sx={{ flex: 1, minWidth: 200, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h5" sx={{ color: 'rgba(76, 175, 80, 0.9)' }}>
                  {formatCurrency(dashboardData.totalRevenue)}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom>
                  Paid Sessions
                </Typography>
                <Typography variant="h5" sx={{ color: 'rgba(76, 175, 80, 0.9)' }}>
                  {dashboardData.totalPaidSessions}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom>
                  Unpaid Sessions
                </Typography>
                <Typography variant="h5" sx={{ color: 'rgba(255, 152, 0, 0.9)' }}>
                  {dashboardData.totalUnpaidSessions}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1, minWidth: 200, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} gutterBottom>
                  Outstanding Amount
                </Typography>
                <Typography variant="h5" sx={{ color: 'rgba(244, 67, 54, 0.9)' }}>
                  {formatCurrency(dashboardData.totalOutstanding)}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Outstanding Payments Section */}
          {dashboardData.outstandingPayments && dashboardData.outstandingPayments.length > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, mb: 2 }}>
                <Typography variant="h6" className="theme-text-primary">
                  Outstanding Payments
                </Typography>
                {selectedClients.size > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleSendBulkPaymentRequests}
                    disabled={sendingBulkRequest}
                    startIcon={sendingBulkRequest ? <CircularProgress size={20} /> : null}
                    sx={{
                      backgroundColor: 'rgba(79, 70, 229, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(79, 70, 229, 1)'
                      }
                    }}
                  >
                    {sendingBulkRequest 
                      ? 'Sending...' 
                      : `Send Payment Requests (${selectedClients.size})`
                    }
                  </Button>
                )}
              </Box>

              {bulkRequestSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBulkRequestSuccess(null)}>
                  {bulkRequestSuccess}
                </Alert>
              )}
              <TableContainer component={Paper} sx={{ mb: 4, backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Checkbox
                          checked={dashboardData.outstandingPayments.length > 0 && selectedClients.size === dashboardData.outstandingPayments.length}
                          indeterminate={selectedClients.size > 0 && selectedClients.size < dashboardData.outstandingPayments.length}
                          onChange={handleSelectAll}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&.Mui-checked': {
                              color: 'rgba(79, 70, 229, 0.9)'
                            },
                            '&.MuiCheckbox-indeterminate': {
                              color: 'rgba(79, 70, 229, 0.9)'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Client</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Payment Type</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Session Date</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Amount</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Days Overdue</TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.outstandingPayments.map((payment) => (
                      <TableRow key={payment._id}>
                        <TableCell padding="checkbox" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <Checkbox
                            checked={selectedClients.has(payment.client._id)}
                            onChange={() => handleSelectClient(payment.client._id)}
                            sx={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              '&.Mui-checked': {
                                color: 'rgba(79, 70, 229, 0.9)'
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          {`${payment.client.firstName} ${payment.client.lastName}`}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <Chip 
                            label={payment.client.paymentDetails?.type?.replace('_', ' ') || 'Per Session'} 
                            size="small" 
                            variant="outlined"
                          />
                          {payment.client.paymentDetails?.employerReference && (
                            <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                              Ref: {payment.client.paymentDetails.employerReference}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{formatDate(payment.sessionDate)}</TableCell>
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{formatCurrency(payment.amount)}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <Chip 
                            label={`${payment.daysOverdue} days`}
                            color={payment.daysOverdue > 30 ? 'error' : payment.daysOverdue > 7 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/client-details/${payment.client._id}`)}
                            sx={{ color: 'rgba(100, 181, 246, 0.9)', borderColor: 'rgba(100, 181, 246, 0.5)', '&:hover': { borderColor: 'rgba(100, 181, 246, 0.9)', backgroundColor: 'rgba(100, 181, 246, 0.1)' } }}
                          >
                            View Client
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Typography variant="h6" gutterBottom className="theme-text-primary">
            Recent Transactions
          </Typography>

          <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Table>
              <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Date</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Client</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Type</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Amount</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Status</TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.recentTransactions.map((transaction) => (
                  <TableRow key={transaction._id}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{formatDate(transaction.createdAt)}</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {`${transaction.client.firstName} ${transaction.client.lastName}`}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {transaction.type === 'block_booking' ? 'Block Booking' : 'Single Session'}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{formatCurrency(transaction.amount, transaction.currency)}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Chip 
                        label={transaction.status} 
                        color={getStatusColor(transaction.status)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      {transaction.status === 'succeeded' && !transaction.refundId && (
                        <Button
                          size="small"
                          onClick={() => {
                            setSelectedTransactionId(transaction._id);
                            setRefundDialogOpen(true);
                          }}
                          sx={{ color: 'rgba(244, 67, 54, 0.9)', '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' } }}
                        >
                          Refund
                        </Button>
                      )}
                      {transaction.refundId && (
                        <Chip 
                          label="Refunded" 
                          color="default" 
                          size="small" 
                          title={transaction.refundReason}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {activeTab === 1 && (
        <PaymentDataIntegrity />
      )}

      <RefundDialog
        open={refundDialogOpen}
        transactionId={selectedTransactionId}
        onClose={() => setRefundDialogOpen(false)}
        onRefund={handleRefund}
      />
    </Box>
  );
};

export default PaymentDashboard;
